import { Avatar } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { routingConstants } from "@/Constants/routingConstants";
import {
  fetchAuthorisedTimesheets,
  updateToggleSelected,
  toggleSelectedAll,
  updateSorting,
  updatePagination,
  setAuthorisedTimesheetsLoading,
  updateSelectedSignOffIds,
  updateSelectedCandidate,
  updateSelectedTimesheets,
} from "@/reducers/timesheets/timesheetauthorised";
import {
  getActionByHtml,
  getColumnNormalHtml,
} from "@/utilities/datatable/columnFunctions";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { apiDecrypt } from "@/utilities/keyEncryption";
import { getComplianceClass } from "@/utilities/utils";

import TableLoader from "../../TableLoader";
import TableNoContent from "../../TableNoContent";
import { Miles } from "../candidates/Listing/common/Miles";
import CandidateStatusAndInfo from "../dataTables/CandidateStatusAndInfo";
import ClientPayPopover from "../dataTables/ClientPayPopover";
import BlurredText from "../managedService/BlurredText";
import Actions from "./Listing/Authorised/Actions";
import CreateInvoiceModal from "./Listing/Authorised/CreateInvoiceModal";
import { FilterModal } from "./Listing/Authorised/FilterModal";
import SearchFilter from "./Listing/Authorised/SearchFilter";
import DataTablePaginationLoader from "../../../Skeletons/DataTablePaginationLoader";
import { SelectedCountText } from "../candidates/Listing/common/SelectedCountText";

const AuthorisedList = () => {
  const dispatch = useDispatch();
  const {
    selectedCandidate,
    toggleSelected,
    isLoading,
    pagination,
    authorisedTimesheets,
    totalCount,
    isSelectedAll,
    selectedSignOffIds,
    selectedTimesheets,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientTimesheetAuthorised);
  const { clientUserAllPermissions } = useSelector((state) => state.session);

  useEffect(() => {
    if (selectedCandidate.length == 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedCandidate]);
  const [data, setData] = useState([]);
  useEffect(() => {
    setData(authorisedTimesheets);
  }, [authorisedTimesheets]);
  const columns = [
    {
      name: "Candidate",
      sortable: true,
      sortField: "candidate_forename",

      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_name"),
      selector: (row) => (
        <div className="flex gap-[14px]">
          <div className="min-w-[45px]">
            <Avatar
              alt={row.candidate_name}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="flex grow flex-col gap-1 overflow-hidden">
            {!clientUserAllPermissions ||
            clientUserAllPermissions.is_allow_to_view_candidate_profile ===
              1 ? (
              <>
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-semibold leading-[normal] text-black"
                >
                  {row.candidate_name}
                </Link>
              </>
            ) : (
              <span className="cursor-default truncate text-[14px] font-semibold leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}
            <CandidateStatusAndInfo
              sortlisted={row.candidate_sortlisted}
              unsuitable={row.candidate_unsuitable}
              is_auto_booked_on_apply={row.is_auto_booked_on_apply}
              extraEle={
                !clientUserAllPermissions ||
                clientUserAllPermissions.is_allow_to_view_contract_detail ===
                  1 ? (
                  <div className="flex flex-wrap items-center gap-1">
                    <Link
                      to={routingConstants.contractDetail(
                        row.route_contract_id,
                      )}
                      className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor"
                    >
                      {row.contract_ref_no}
                    </Link>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <span className="line-clamp-1 cursor-default text-[12px] font-medium leading-[normal] text-txtColor">
                      {row.contract_ref_no}
                    </span>
                  </div>
                )
              }
            />
          </div>
        </div>
      ),
      width: "17%",
    },
    {
      name: "Contact",
      sortable: true,
      sortField: "candidate_email",
      selector: (row) => (
        <div className="flex flex-col">
          <BlurredText type="phone">
            {getColumnNormalHtml(row.mobile_phone_number, "truncate")}
          </BlurredText>
          <BlurredText type="email">
            {getColumnNormalHtml(row.candidate_email, "truncate")}
          </BlurredText>
        </div>
      ),
      width: "14%",
    },
    {
      name: "Miles",
      sortable: false,
      selector: (row) => <Miles miles={row.miles} />,
      width: "7.3%",
    },
    {
      name: "Role",
      sortable: true,
      sortField: "role_name",
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          <span className="truncate text-[12px] font-medium leading-[normal] text-txtColor">
            {row.license_number}
          </span>
        </div>
      ),
      width: "8%",
    },
    {
      name: "Authorised by",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getActionByHtml(
            row.actioned_by,
            row.actioned_at,
            row.actioned_from,
            "",
          )}
        </div>
      ),
      width: "12.6%",
    },
    {
      name: "Shift Date",
      sortable: true,
      sortField: "shift_date",
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            moment(row.shift_date, "DD/MM/YYYY").format("DD/MM/YYYY"),
          )}
          <span
            className={`h-[23px] w-[75px] rounded-[4px] text-center text-[14px] font-medium leading-[23px] ${getComplianceClass(
              row.profile_percent,
            )}`}
          >
            {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
          </span>
        </div>
      ),
      width: "7.5%",
    },
    {
      name: "Shift",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col items-end gap-1">
          {getColumnNormalHtml(
            moment(row.start_time, "HH:mm:ss").format("HH:mm"),
          )}
          <span className="text-[14px] font-semibold leading-[0px] text-black">
            -
          </span>
          {getColumnNormalHtml(
            moment(row.end_time, "HH:mm:ss").format("HH:mm"),
          )}
        </div>
      ),
      width: "50px",
    },
    {
      name: "Break/Admin",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col items-end gap-1">
          {getColumnNormalHtml(
            moment(row.break_time, "HH:mm:ss").format("HH:mm"),
          )}
          <span className="text-[14px] font-semibold leading-[0px] text-black">
            -
          </span>
          {getColumnNormalHtml(
            moment(row.admin_time, "HH:mm:ss").format("HH:mm"),
          )}
        </div>
      ),
      width: "8%",
    },
    {
      name: "Visit",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col items-end gap-1">
          <span className="text-[14px] font-semibold leading-[normal] text-black">
            {row.no_of_visit ? row.no_of_visit : 0}
          </span>
        </div>
      ),
      width: "50px",
    },
    {
      name: "Unit",
      sortable: false,
      selector: (row) => (
        <div className="flex flex-col gap-1" data-tag="allowRowEvents">
          <span className="text-[14px] font-semibold leading-[normal] text-black">
            {Number(`${row.units}`).toFixed(2)}
          </span>
        </div>
      ),
      width: "60px",
    },
    {
      name: "Total",
      sortable: false,
      selector: (row) => (
        <ClientPayPopover
          pay_arr={row?.client_pay_arr}
          shift_rate={
            Number(row.shift_rate) +
            Number(row.cover_request_awarded_reward || 0) -
            Number(row.cover_request_awarded_penalty || 0)
          }
          is_substitute_contract={
            row.cover_request_awarded_penalty != "0"
              ? 1
              : row.cover_request_awarded_reward != "0"
                ? 1
                : 0
          }
          cover_request_awarded_penalty={row?.cover_request_awarded_penalty}
          cover_request_awarded_reward={row.cover_request_awarded_reward}
          content={(content) => (
            <div className="flex flex-col gap-1" data-tag="allowRowEvents">
              {getColumnNormalHtml(content, "cursor-pointer")}
            </div>
          )}
        />
      ),
      width: "auto",
    },
  ];
  const dataTableRef = useRef();

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchAuthorisedTimesheets({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    // dispatch(deSelectAllTimesheets())
    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setAuthorisedTimesheetsLoading(true));
    dispatch(fetchAuthorisedTimesheets());
  };

  return (
    <div className="AuthorisedList_Wrapper mb-[58px]">
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="main-heading tour-authorised-heading font-medium leading-[normal] text-black">
              Authorised Timesheet
            </h4>
            <Actions />
            <SelectedCountText
              selectedCount={selectedSignOffIds.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedTimesheets(val));
              }}
              currentDataLength={data.length}
            />
          </div>
          <div className="flex gap-[20px]">
            <SearchFilter />
            <FilterModal />
          </div>
        </div>
        <div
          className="DataTable_Wrapper hide_extended_arrow"
          ref={dataTableRef}
        >
          <DataTable
            columns={columns}
            data={data}
            fixedHeader
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeaderScrollHeight="600px"
            progressPending={isLoading}
            progressComponent={<TableLoader rows={5} />}
            selectableRows
            onSelectedRowsChange={(state) => {
              let selectedSignOff = state.selectedRows.map((item) => {
                return item.timesheet_id;
              });
              let selectedIDS = state.selectedRows.map((item) => {
                let can_ids = parseInt(apiDecrypt(item.route_id));
                return can_ids;
              });
              dispatch(updateSelectedCandidate(selectedIDS));
              dispatch(updateSelectedSignOffIds(selectedSignOff));
            }}
            noDataComponent={<TableNoContent rows={5} />}
            clearSelectedRows={toggleSelected}
            // expandableRows
            // expandOnRowClicked
            // expandableRowsComponent={Expandedrow}
            className="_DataTable"
          />
        </div>
        {!isLoading && isPaginationLoading ? (
          <DataTablePaginationLoader />
        ) : null}
        <CreateInvoiceModal />
      </div>
    </div>
  );
};

export default AuthorisedList;
